import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { youtubeCuepointsMessages } from '../messages/youtube-cuepoints-messages';
import { useInsertYoutubeCuepoint } from './useInsertYoutubeCuepoint';

const MIN_DURATION_SECS = 1;
const MAX_DURATION_SECS = 300;

interface InsertCuepointFormValues {
  durationSecs: number;
}

export interface InsertCuepointDialogProps {
  open: boolean;
  onClose: () => void;
  stationId: string;
  listId: string;
}

/**
 * Dialog to insert a manual YouTube ad cuepoint by specifying its duration in seconds.
 */
export function InsertCuepointDialog({
  open,
  onClose,
  stationId,
  listId,
}: InsertCuepointDialogProps) {
  const { $t } = useIntl();
  const { insertCuepoint, isPending } = useInsertYoutubeCuepoint({
    stationId,
    listId,
    onSuccess: onClose,
  });

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  const handleSubmit = async ({ durationSecs }: InsertCuepointFormValues) => {
    await insertCuepoint(Number(durationSecs));
  };

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={handleClose}>
      <FormContainer<InsertCuepointFormValues>
        defaultValues={{ durationSecs: 30 }}
        onSuccess={handleSubmit}
      >
        <DialogTitle>{$t(youtubeCuepointsMessages.insertDialogTitle)}</DialogTitle>
        <DialogContent>
          <TextFieldElement
            name="durationSecs"
            label={$t(youtubeCuepointsMessages.insertDurationLabel)}
            type="number"
            autoFocus
            disabled={isPending}
            inputProps={{
              min: MIN_DURATION_SECS,
              max: MAX_DURATION_SECS,
              step: 1,
            }}
            helperText={$t(youtubeCuepointsMessages.insertDurationHelper)}
            rules={{
              required: $t(youtubeCuepointsMessages.insertDurationRequired),
              min: {
                value: MIN_DURATION_SECS,
                message: $t(youtubeCuepointsMessages.insertDurationMin),
              },
              max: {
                value: MAX_DURATION_SECS,
                message: $t(youtubeCuepointsMessages.insertDurationMax),
              },
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleClose} disabled={isPending}>
            {$t({
              id: 'common.actions.cancel',
              defaultMessage: 'Cancelar',
            })}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isPending}>
            {$t(youtubeCuepointsMessages.insertSubmit)}
          </LoadingButton>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
}
